import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Drawer, FormControl, Grid, IconButton, InputLabel,
  MenuItem, Pagination, Paper, Select, Stack, TextField, Typography,
} from "@mui/material";
import EuroIcon from "@mui/icons-material/Euro";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PsychologyIcon from "@mui/icons-material/Psychology";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "react-toastify";
import api from "../../api";
import AnalyticsPageShell, { CARD_SX } from "./AnalyticsPageShell";
import AnalyticsDataTable from "./AnalyticsDataTable";
import StatusChip from "../../components/admin/StatusChip";
import LoadError from "../../components/admin/LoadError";
import { formatDateTime } from "../../utils/adminFormat";

/**
 * Analytics › AI Usage — every AI call the platform makes: cost per feature and
 * model, cost per day by provider, the models each setting points to, and the
 * credit / payment / limit status of every AI account.
 * API: GET admin/analytics/ai-usage, …/calls, …/accounts; POST …/accounts/check.
 */

const PROVIDERS = {
  gemini: { label: "Gemini", color: "#2563eb" },
  openai: { label: "OpenAI", color: "#0f766e" },
  anthropic: { label: "Claude", color: "#ea580c" },
  xai: { label: "Grok", color: "#475569" },
  deepseek: { label: "DeepSeek", color: "#7c3aed" },
  datalab: { label: "DataLab", color: "#9ca3af" },
};

const ACCOUNT_STATUS = {
  ok: { label: "Working", tone: "green" },
  no_credit: { label: "Payment needed", tone: "red" },
  rate_limited: { label: "Rate limited", tone: "amber" },
  bad_key: { label: "Key rejected", tone: "red" },
  not_set: { label: "Not set up", tone: "grey" },
  error: { label: "Check failed", tone: "amber" },
};

const LOW_BALANCE_USD = 5;

const PAGE_TITLE = "AI Usage & Cost";
const PAGE_SUBTITLE =
  "Every AI call the platform makes — which model, which feature, how many tokens and what it cost — and whether each AI account has credit.";

function ProviderDot({ provider }) {
  return (
    <Box component="span" sx={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", bgcolor: PROVIDERS[provider]?.color || "#9ca3af", flexShrink: 0 }} />
  );
}

/** "StructureQuestionsFromMarkdownJob::handle < X::y" → "Structure questions from markdown job". */
function featureName(feature) {
  if (!feature || feature === "unknown") return "Unknown caller";
  const cls = feature.split(" < ")[0].split("::")[0];
  const words = cls.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/([A-Z])([A-Z][a-z])/g, "$1 $2").toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function compact(n) {
  if (!n) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function KpiTile({ label, value, note, Icon, iconBg, iconColor }) {
  return (
    <Paper sx={{ ...CARD_SX, display: "flex", alignItems: "center", gap: 2, height: "100%" }}>
      <Box sx={{ width: 52, height: 52, borderRadius: "50%", bgcolor: iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon sx={{ color: iconColor, fontSize: 26 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{value}</Typography>
        {note && <Typography variant="caption" color="text.secondary">{note}</Typography>}
      </Box>
    </Paper>
  );
}

function Section({ title, subtitle, action, children }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between", gap: 1, mb: 1 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
          {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
        </Box>
        {action}
      </Box>
      {children}
    </Box>
  );
}

export default function AiUsageAnalytics() {
  const [days, setDays] = useState(30);
  const [provider, setProvider] = useState("");
  const [model, setModel] = useState("");
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [accounts, setAccounts] = useState(null);
  const [checking, setChecking] = useState(false);

  const [drawerFeature, setDrawerFeature] = useState(null);
  const [showAllFeatures, setShowAllFeatures] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { days };
      if (provider) params.provider = provider;
      if (model) params.model = model;
      if (q) params.q = q;
      const res = await api.get("admin/analytics/ai-usage", { params });
      setData(res.data?.data || null);
    } catch (e) {
      setError(e);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [days, provider, model, q]);

  const loadAccounts = useCallback(async () => {
    try {
      const res = await api.get("admin/analytics/ai-usage/accounts");
      setAccounts(res.data?.data || null);
    } catch {
      setAccounts(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  const checkNow = async () => {
    try {
      setChecking(true);
      const res = await api.post("admin/analytics/ai-usage/accounts/check");
      setAccounts(res.data?.data || null);
      toast.success("AI accounts checked");
    } catch (e) {
      toast.error(e.response?.status === 429 ? "Checked very recently — try again in a minute." : (e.response?.data?.message || "Check failed"));
    } finally {
      setChecking(false);
    }
  };

  const eur = data?.usd_to_eur ?? accounts?.usd_to_eur ?? 0.92;
  const money = useCallback((usd, priced = true) => {
    if (!priced) return "no price";
    const v = (usd || 0) * eur;
    if (v > 0 && v < 0.01) return "<€0.01";
    return `€${v.toFixed(2)}`;
  }, [eur]);

  const chartData = useMemo(() => {
    if (!data) return [];
    const byDay = {};
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - (data.days - 1));
    for (let i = 0; i < data.days; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      byDay[key] = { day: d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }) };
    }
    (data.by_day || []).forEach((r) => {
      if (byDay[r.day]) byDay[r.day][r.provider] = +((r.cost_usd || 0) * eur).toFixed(4);
    });
    return Object.values(byDay);
  }, [data, eur]);

  const chartProviders = useMemo(
    () => Object.keys(PROVIDERS).filter((p) => (data?.by_provider || []).some((r) => r.provider === p)),
    [data]
  );

  const t = data?.totals;
  const thinkingPct = t?.thinking_share != null ? `${Math.round(t.thinking_share * 100)}%` : "—";
  const needsPayment = accounts?.needs_payment || [];

  const modelColumns = [
    {
      key: "model", label: "Model", render: (r) => (
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography component="span" sx={{ fontFamily: "monospace", fontSize: 13 }}>{r.model}</Typography>
          {r.tier === "premium" && <StatusChip tone="amber" label="Premium" />}
        </Box>
      ),
    },
    { key: "provider", label: "Provider", render: (r) => <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}><ProviderDot provider={r.provider} />{PROVIDERS[r.provider]?.label || r.provider}</Box> },
    { key: "used_by", label: "Used by", render: (r) => `${(r.used_by || []).map(featureName).join(", ")}${r.feature_count > 3 ? ` +${r.feature_count - 3}` : ""}` },
    { key: "calls", label: "Calls", render: (r) => `${r.calls.toLocaleString("en-GB")}${r.failed ? ` (${r.failed} failed)` : ""}` },
    { key: "price", label: "Price in / out per 1M", render: (r) => (r.price ? `$${Number(r.price.in).toFixed(2)} / $${Number(r.price.out).toFixed(2)}` : "no price set") },
    { key: "cost_usd", label: "Cost", render: (r) => <strong>{money(r.cost_usd, !!r.price)}</strong> },
    {
      key: "share", label: "Share", render: (r) => (
        <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 110 }}>
          <Box component="span" sx={{ flex: 1, height: 8, bgcolor: "#f1f2f6", borderRadius: 4 }}>
            <Box component="span" sx={{ display: "block", width: `${Math.round(r.share * 100)}%`, height: 8, borderRadius: 4, bgcolor: PROVIDERS[r.provider]?.color }} />
          </Box>
          <Typography component="span" variant="caption">{Math.round(r.share * 100)}%</Typography>
        </Box>
      ),
    },
  ];

  const featureColumns = [
    {
      key: "feature", label: "Feature", render: (r) => (
        <Box component="span" sx={{ display: "block" }}>
          <Typography component="span" variant="body2" sx={{ display: "block", fontWeight: 600 }}>{featureName(r.feature)}</Typography>
          <Typography component="span" variant="caption" color="text.secondary" sx={{ display: "block", fontFamily: "monospace", wordBreak: "break-all" }}>{r.feature}</Typography>
        </Box>
      ),
    },
    { key: "model", label: "Model", render: (r) => <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, fontFamily: "monospace", fontSize: 13 }}><ProviderDot provider={r.provider} />{r.model}</Box> },
    { key: "calls", label: "Calls", render: (r) => r.calls.toLocaleString("en-GB") },
    {
      key: "failed", label: "Failed", render: (r) => (r.no_credit
        ? <StatusChip tone="red" label={`${r.failed} · ${r.no_credit} no credit`} />
        : r.failed ? <StatusChip tone="amber" label={String(r.failed)} /> : "0"),
    },
    { key: "prompt_tokens", label: "Input", render: (r) => compact(r.prompt_tokens) },
    { key: "output_tokens", label: "Output", render: (r) => compact(r.output_tokens) },
    { key: "thinking_tokens", label: "Thinking", render: (r) => compact(r.thinking_tokens) },
    { key: "cost_usd", label: "Cost", render: (r) => <strong>{money(r.cost_usd, r.priced || r.calls === r.failed)}</strong> },
    { key: "share", label: "%", render: (r) => `${(r.share * 100).toFixed(1)}%` },
  ];

  const configuredColumns = [
    { key: "use", label: "Used for" },
    { key: "setting", label: "Setting", render: (r) => <Typography component="span" sx={{ fontFamily: "monospace", fontSize: 12.5 }}>{r.setting}</Typography> },
    { key: "model", label: "Model", render: (r) => <Typography component="span" sx={{ fontFamily: "monospace", fontSize: 13 }}>{r.model}</Typography> },
    { key: "note", label: "Note", render: (r) => (r.note ? <StatusChip tone="amber" label={r.note} /> : "") },
  ];

  const features = data?.features || [];
  const shownFeatures = showAllFeatures ? features : features.slice(0, 25);

  return (
    <AnalyticsPageShell
      title={PAGE_TITLE}
      subtitle={PAGE_SUBTITLE}
      filters={
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Date range</InputLabel>
              <Select label="Date range" value={days} onChange={(e) => setDays(e.target.value)}>
                <MenuItem value={1}>Today</MenuItem>
                <MenuItem value={7}>Last 7 days</MenuItem>
                <MenuItem value={30}>Last 30 days</MenuItem>
                <MenuItem value={90}>Last 90 days</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Provider</InputLabel>
              <Select label="Provider" value={provider} onChange={(e) => setProvider(e.target.value)}>
                <MenuItem value="">All providers</MenuItem>
                {(data?.filters?.providers || []).map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Model</InputLabel>
              <Select label="Model" value={model} onChange={(e) => setModel(e.target.value)}>
                <MenuItem value="">All models</MenuItem>
                {(data?.filters?.models || []).map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth size="small" label="Feature" placeholder="e.g. extraction" value={search} onChange={(e) => setSearch(e.target.value)} />
          </Grid>
        </Grid>
      }
    >
      {needsPayment.length > 0 && (
        <Alert severity="error" sx={{ mb: 2, alignItems: "center" }}
          action={<Button color="inherit" size="small" href="#ai-accounts" sx={{ fontWeight: 600 }}>See accounts</Button>}>
          <strong>{needsPayment.length === 1 ? "1 AI account needs payment" : `${needsPayment.length} AI accounts need payment`}:</strong>{" "}
          {needsPayment.join(", ")}. Every call to {needsPayment.length === 1 ? "it" : "them"} fails until credit is added.
        </Alert>
      )}

      <Box id="ai-accounts">
        <Section
          title="AI accounts"
          subtitle={accounts?.checked_at
            ? `Credit, payment and limit status. Last checked ${formatDateTime(accounts.checked_at)} · checked automatically every hour.`
            : "Not checked yet — press Check now."}
          action={
            <Button variant="outlined" size="small" startIcon={checking ? <CircularProgress size={14} /> : <RefreshIcon />}
              onClick={checkNow} disabled={checking} sx={{ textTransform: "none" }}>
              Check now
            </Button>
          }
        >
          <Grid container spacing={2}>
            {(accounts?.accounts || []).map((a) => {
              const balanceNum = a.balance ? parseFloat(String(a.balance).replace(/[^0-9.]/g, "")) : null;
              const low = a.status === "ok" && balanceNum != null && balanceNum < LOW_BALANCE_USD;
              const st = low ? { label: "Low balance", tone: "amber" } : (ACCOUNT_STATUS[a.status] || ACCOUNT_STATUS.error);
              const bad = ["no_credit", "bad_key"].includes(a.status);
              return (
                <Grid item xs={12} sm={6} lg={4} key={a.key}>
                  <Paper sx={{ ...CARD_SX, height: "100%", display: "flex", flexDirection: "column", gap: 1, ...(bad ? { borderColor: "#FCA5A5", bgcolor: "#FEF2F2" } : {}) /* red: payment needed / key rejected (3 Nov 2026) */ }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, fontWeight: 700 }}><ProviderDot provider={a.key} />{a.provider}</Box>
                      <StatusChip tone={st.tone} label={st.label} />
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}><Typography variant="body2" color="text.secondary">Balance</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{a.balance || "Not shown by provider"}</Typography></Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}><Typography variant="body2" color="text.secondary">Spent this month</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{a.spent_month_usd != null ? money(a.spent_month_usd) : "—"}</Typography></Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}><Typography variant="body2" color="text.secondary">Limit hits (7 days)</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {a.no_credit_hits_7d || a.rate_limit_hits_7d
                          ? [a.no_credit_hits_7d ? `${a.no_credit_hits_7d} no credit` : null, a.rate_limit_hits_7d ? `${a.rate_limit_hits_7d} busy` : null].filter(Boolean).join(" · ")
                          : "0"}
                      </Typography>
                    </Box>
                    {a.limits && <Typography variant="caption" color="text.secondary">{a.limits}</Typography>}
                    <Typography variant="caption" color="text.secondary">{a.detail}</Typography>
                    {(bad || low) && a.top_up && (
                      <Button variant="contained" color="warning" size="small" href={a.top_up} target="_blank" rel="noopener noreferrer"
                        sx={{ alignSelf: "flex-start", textTransform: "none", mt: "auto" }}>
                        Add credit
                      </Button>
                    )}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Section>
      </Box>

      {error ? (
        <LoadError what="AI usage" error={error} onRetry={load} />
      ) : loading && !data ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
      ) : data && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} lg={3}>
              <KpiTile label={`Total cost (${data.days === 1 ? "today" : `${data.days} days`})`} value={money(t.cost_usd)}
                note={(data.by_provider || []).filter((p) => p.cost_usd > 0).map((p) => `${p.label} ${money(p.cost_usd)}`).join(" · ") || "Estimated from token prices"}
                Icon={EuroIcon} iconBg="#ede9fe" iconColor="#7c3aed" />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <KpiTile label="AI calls" value={t.calls.toLocaleString("en-GB")}
                note={t.unpriced ? `${t.unpriced} on models with no price set` : null}
                Icon={ShowChartIcon} iconBg="#dbeafe" iconColor="#2563eb" />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <KpiTile label="Failed / timed-out calls" value={t.failed.toLocaleString("en-GB")}
                note={t.failed ? [t.no_credit && `${t.no_credit} no credit`, t.rate_limited && `${t.rate_limited} busy`, t.timeouts && `${t.timeouts} timed out (still billed)`].filter(Boolean).join(" · ") : "None"}
                Icon={WarningAmberIcon} iconBg="#fff7ed" iconColor="#c2410c" />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <KpiTile label={'Hidden "thinking" tokens'} value={thinkingPct} note="of output tokens — billed as output"
                Icon={PsychologyIcon} iconBg="#dcfce7" iconColor="#15803d" />
            </Grid>
          </Grid>

          <Section title="Cost per day (€)">
            <Paper sx={{ ...CARD_SX, height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0f4" />
                  <XAxis dataKey="day" tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={16} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `€${v}`} />
                  <Tooltip formatter={(v, name) => [`€${Number(v).toFixed(2)}`, PROVIDERS[name]?.label || name]} />
                  <Legend formatter={(v) => PROVIDERS[v]?.label || v} />
                  {chartProviders.map((p) => <Bar key={p} dataKey={p} stackId="cost" fill={PROVIDERS[p].color} />)}
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Section>

          <Section title="Models in use" subtitle="Every model actually called in this period, biggest cost first.">
            <AnalyticsDataTable columns={modelColumns} rows={data.models} emptyMessage="No AI calls in this period." />
          </Section>

          <Section title="Cost by feature" subtitle="Which screen or job made the calls. View shows the recent calls.">
            <AnalyticsDataTable
              columns={featureColumns}
              rows={shownFeatures.map((r, i) => ({ ...r, id: `${r.feature}|${r.model}|${i}` }))}
              emptyMessage="No AI calls in this period."
              renderActions={(r) => (
                <Button size="small" variant="outlined" sx={{ textTransform: "none" }} onClick={() => setDrawerFeature(r)}>View</Button>
              )}
            />
            {features.length > 25 && (
              <Button sx={{ mt: 1, textTransform: "none" }} onClick={() => setShowAllFeatures((v) => !v)}>
                {showAllFeatures ? "Show top 25" : `Show all ${features.length} features`}
              </Button>
            )}
          </Section>

          <Section title="Configured models" subtitle="What each setting points to on this server right now. Read-only — change it in .env. API keys are never shown.">
            <AnalyticsDataTable columns={configuredColumns} rows={(data.configured || []).map((r) => ({ ...r, id: r.setting }))} />
          </Section>
        </>
      )}

      <FeatureDrawer feature={drawerFeature} days={days} money={money} onClose={() => setDrawerFeature(null)} />
    </AnalyticsPageShell>
  );
}

function FeatureDrawer({ feature, days, money, onClose }) {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const perPage = 25;

  useEffect(() => { setPage(1); }, [feature]);

  const load = useCallback(async () => {
    if (!feature) return;
    try {
      setLoading(true);
      setError(null);
      const res = await api.get("admin/analytics/ai-usage/calls", {
        params: { feature: feature.feature, model: feature.model, days, page, per_page: perPage },
      });
      setRows(res.data?.data?.data || []);
      setTotal(res.data?.data?.total || 0);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [feature, days, page]);

  useEffect(() => { load(); }, [load]);

  const f = feature;
  const succeeded = f ? f.calls - f.failed : 0;
  const tokenTotal = f ? f.prompt_tokens + f.output_tokens + f.thinking_tokens : 0;
  const pct = (n) => (tokenTotal ? Math.round((n / tokenTotal) * 100) : 0);

  return (
    <Drawer anchor="right" open={!!feature} onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", sm: 560 } } }}>
      {f && (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", p: 2.5, borderBottom: "1px solid #eef0f4" }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: ".04em", color: "text.secondary" }}>FEATURE</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{featureName(f.feature)}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>{f.feature}</Typography>
            </Box>
            <IconButton aria-label="Close" onClick={onClose}><CloseIcon /></IconButton>
          </Box>

          <Box sx={{ p: 2.5, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
            <Grid container spacing={1.5}>
              {[
                [`Cost (${days === 1 ? "today" : `${days} days`})`, money(f.cost_usd)],
                ["Calls", f.calls.toLocaleString("en-GB")],
                ["Per call", succeeded ? money(f.cost_usd / succeeded) : "—"],
              ].map(([label, value]) => (
                <Grid item xs={4} key={label}>
                  <Box sx={{ bgcolor: "#f9fafb", borderRadius: 2, p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{value}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}><Typography variant="body2" color="text.secondary">Model</Typography><Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 600 }}>{f.model}</Typography></Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}><Typography variant="body2" color="text.secondary">Provider</Typography><Typography variant="body2">{PROVIDERS[f.provider]?.label || f.provider}</Typography></Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}><Typography variant="body2" color="text.secondary">Avg tokens per call</Typography>
                <Typography variant="body2">{succeeded ? `${compact(Math.round(f.prompt_tokens / succeeded))} in · ${compact(Math.round(f.output_tokens / succeeded))} out · ${compact(Math.round(f.thinking_tokens / succeeded))} thinking` : "—"}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}><Typography variant="body2" color="text.secondary">Failed / timed out</Typography><Typography variant="body2" sx={{ fontWeight: 600, color: f.failed ? "#9a3412" : "inherit" }}>{f.failed}{f.no_credit ? ` (${f.no_credit} no credit)` : ""}</Typography></Box>
              {f.avg_ms != null && <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}><Typography variant="body2" color="text.secondary">Avg time taken</Typography><Typography variant="body2">{(f.avg_ms / 1000).toFixed(1)} s</Typography></Box>}
            </Paper>

            {tokenTotal > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Tokens by kind</Typography>
                <Box sx={{ display: "flex", height: 14, borderRadius: 7, overflow: "hidden", bgcolor: "#f1f2f6" }}>
                  <Box sx={{ width: `${pct(f.prompt_tokens)}%`, bgcolor: "#93c5fd" }} />
                  <Box sx={{ width: `${pct(f.output_tokens)}%`, bgcolor: "#2563eb" }} />
                  <Box sx={{ width: `${pct(f.thinking_tokens)}%`, bgcolor: "#1e3a8a" }} />
                </Box>
                <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: "wrap" }}>
                  <Typography variant="caption">Input {pct(f.prompt_tokens)}%</Typography>
                  <Typography variant="caption">Answer {pct(f.output_tokens)}%</Typography>
                  <Typography variant="caption">Thinking {pct(f.thinking_tokens)}%</Typography>
                </Stack>
              </Box>
            )}

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Recent calls</Typography>
              {error ? (
                <LoadError what="recent calls" error={error} onRetry={load} />
              ) : (
                <AnalyticsDataTable
                  loading={loading}
                  emptyMessage="No calls in this period."
                  rows={rows}
                  columns={[
                    { key: "created_at", label: "Time", render: (r) => <Box component="span" sx={{ whiteSpace: "nowrap" }}>{new Date(r.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</Box> },
                    {
                      key: "http_status", label: "Status", render: (r) => (r.error_type
                        ? <StatusChip tone={r.error_type === "rate_limited" ? "amber" : "red"} label={{ no_credit: "No credit", rate_limited: "Busy (429)", timeout: "Timed out", bad_key: "Key rejected" }[r.error_type] || `Error ${r.http_status}`} />
                        : <StatusChip tone="green" label="OK" />),
                    },
                    { key: "prompt_tokens", label: "In", render: (r) => (r.prompt_tokens != null ? r.prompt_tokens.toLocaleString("en-GB") : "—") },
                    { key: "output_tokens", label: "Out", render: (r) => (r.output_tokens != null ? r.output_tokens.toLocaleString("en-GB") : "—") },
                    { key: "thinking_tokens", label: "Think", render: (r) => (r.thinking_tokens != null ? r.thinking_tokens.toLocaleString("en-GB") : "—") },
                    { key: "cost_usd", label: "Cost", render: (r) => (r.cost_usd != null ? money(Number(r.cost_usd)) : (r.error_type === "timeout" ? "billed" : "—")) },
                    { key: "duration_ms", label: "Time taken", render: (r) => (r.duration_ms != null ? `${(r.duration_ms / 1000).toFixed(1)} s` : "—") },
                  ]}
                />
              )}
              {total > perPage && (
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                  <Pagination size="small" count={Math.ceil(total / perPage)} page={page} onChange={(_, p) => setPage(p)} />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Drawer>
  );
}
